<template>
  <div class="stats-grid">
    <!-- Dynamic Stats Grid -->
    <div class="grid gap-6" :style="gridStyle">
      <component
        v-for="(card, index) in statsConfig"
        :key="`stat-card-${index}`"
        :is="getCardComponent(card.type)"
        :config="card"
        :data="getCardData(card.dataKey)"
        :class="getCardClasses(card)"
        @card-click="handleCardClick"
      />
    </div>
  </div>
</template>

<script>
// Import all stat card components
import SimpleStatCard from "./statCards/SimpleStatCard.vue";
import DonutStatCard from "./statCards/DonutStatCard.vue";
import ProgressStatCard from "./statCards/ProgressStatCard.vue";
import ListStatCard from "./statCards/ListStatCard.vue";
import TableStatCard from "./statCards/TableStatCard.vue";
import ChartStatCard from "./statCards/ChartStatCard.vue";
import MetricGridCard from "./statCards/MetricGridCard.vue";

export default {
  name: "StatsGrid",
  components: {
    SimpleStatCard,
    DonutStatCard,
    ProgressStatCard,
    ListStatCard,
    TableStatCard,
    ChartStatCard,
    MetricGridCard
  },
  props: {
    analytics: {
      type: Object,
      required: true
    },
    gridColumns: {
      type: Number,
      default: 3
    },
    customConfig: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      // Performance optimization: Cache computed values
      componentCache: new Map(),
      dataCache: new Map()
    };
  },
  computed: {
    gridStyle() {
      return {
        gridTemplateColumns: `repeat(auto-fit, minmax(320px, 1fr))`,
        maxWidth: "100%"
      };
    },

    statsConfig() {
      // Use custom config if provided, otherwise use default
      return this.customConfig.length > 0
        ? this.customConfig
        : this.defaultStatsConfig;
    },

    defaultStatsConfig() {
      return [
        // Contacts with Progress Bar
        {
          type: "simple",
          title: "Contacts",
          dataKey: "contacts",
          size: "medium",
          layout: "vertical",
          showDetailedProgress: true,
          metrics: [
            {
              key: "total",
              label: "Total Contacts",
              icon: "contacts",
              color: "blue"
            }
          ],
          progressItems: [
            { key: "totalClients", label: "Total Clients", color: "blue" },
            { key: "totalProspects", label: "Total Prospect", color: "green" },
            { key: "totalEnquiries", label: "Total Enquires", color: "yellow" }
          ]
        },
        // Templates Section
        {
          type: "metric-grid",
          title: "Templates",
          dataKey: "templates",
          size: "medium",
          metrics: [
            { key: "sms", label: "SMS Template", icon: "sms", color: "blue" },
            {
              key: "email",
              label: "Email Template",
              icon: "email",
              color: "green"
            }
          ]
        },

        // Lead Forms Donut
        {
          type: "donut",
          title: "Lead Forms",
          dataKey: "leadForms",
          size: "medium",
          centerValue: "total",
          centerLabel: "Lead Forms",
          segments: [
            { key: "active", label: "Active Lead Form", color: "#10B981" },
            { key: "inactive", label: "Inactive Lead Form", color: "#EF4444" }
          ],
          showLegend: true
        },
        // Agents with Donut Chart
        {
          type: "donut",
          title: "Agents",
          dataKey: "agents",
          size: "medium",
          centerValue: "total",
          segments: [
            { key: "super", label: "Super Agents", color: "#3B82F6" },
            { key: "sub", label: "Sub Agents", color: "#8B5CF6" }
          ]
        },

        // SMS Usage - Enhanced Design
        {
          type: "simple",
          title: "SMS Usage",
          dataKey: "smsUsage",
          size: "medium",
          layout: "usage-card",
          showUsageProgress: true,
          primaryColor: "blue",
          badge: "Mobile Network",
          badgeColor: "blue",
          metrics: [
            {
              key: "totalMessages",
              label: "Total Messages",
              icon: "chat",
              color: "blue"
            },
            {
              key: "delivered",
              label: "Delivered",
              icon: "check_circle",
              color: "green"
            },
            { key: "failed", label: "Failed", icon: "error", color: "red" },
            {
              key: "successRate",
              label: "Success Rate (%)",
              icon: "trending_up",
              color: "purple"
            }
          ]
        },

        // WhatsApp Usage - Enhanced Design
        {
          type: "simple",
          title: "WhatsApp Usage",
          dataKey: "whatsappUsage",
          size: "medium",
          badge: "Business API",
          badgeColor: "green",
          layout: "usage-card",
          showUsageProgress: true,
          primaryColor: "green",
          metrics: [
            {
              key: "totalChats",
              label: "Total Chats",
              icon: "forum",
              color: "green"
            },
            {
              key: "delivered",
              label: "Delivered",
              icon: "check_circle",
              color: "blue"
            },
            { key: "failed", label: "Failed", icon: "error", color: "red" },
            {
              key: "successRate",
              label: "Success Rate (%)",
              icon: "trending_up",
              color: "purple"
            }
          ]
        },
        // Email Usage - Enhanced Design
        {
          type: "simple",
          title: "Email Usage",
          dataKey: "emailUsage",
          size: "medium",
          layout: "usage-card",
          showUsageProgress: true,
          primaryColor: "purple",
          badge: "SMTP Server(Sendgrid)",
          badgeColor: "purple",
          metrics: [
            {
              key: "totalEmails",
              label: "Total Emails",
              icon: "mail",
              color: "purple"
            },
            {
              key: "delivered",
              label: "Delivered",
              icon: "check_circle",
              color: "green"
            },
            { key: "failed", label: "Failed", icon: "error", color: "red" },
            {
              key: "bounceRate",
              label: "Bounce Rate (%)",
              icon: "warning",
              color: "orange"
            }
          ]
        },

        // Email Campaign Status
        {
          type: "donut",
          title: "Email Campaign By Status",
          dataKey: "emailCampaign",
          size: "medium",
          centerValue: "total",
          segments: [
            { key: "draft", label: "Draft", color: "#94A3B8" },
            { key: "completed", label: "Completed", color: "#10B981" },
            { key: "scheduled", label: "Scheduled", color: "#3B82F6" }
          ],
          showLegend: true
        },

        // WhatsApp Campaign Status
        {
          type: "donut",
          title: "WhatsApp Campaign By Status",
          dataKey: "whatsappCampaign",
          size: "medium",
          centerValue: "total",
          segments: [
            { key: "draft", label: "Draft", color: "#94A3B8" },
            { key: "completed", label: "Completed", color: "#10B981" },
            { key: "scheduled", label: "Scheduled", color: "#3B82F6" }
          ],
          showLegend: true
        },
        // Quotation Metrics
        {
          type: "metric-grid",
          title: "Quotation",
          dataKey: "quotation",
          size: "medium",
          layout: "grid-1x4",
          metrics: [
            {
              key: "template",
              label: "Quotation Template",
              icon: "description",
              color: "purple"
            },
            {
              key: "created",
              label: "Quotation Created",
              icon: "create",
              color: "blue"
            },
            {
              key: "sent",
              label: "Quotation Sent",
              icon: "send",
              color: "green"
            }
          ]
        },

        // Email Campaign Delivery
        // {
        //   type: "list",
        //   title: "Email Campaign By Delivery",
        //   dataKey: "emailDelivery",
        //   size: "medium",
        //   items: [
        //     {
        //       key: "sent",
        //       label: "Email Campaign Sent",
        //       icon: "send",
        //       showPercentage: false
        //     },
        //     {
        //       key: "delivered",
        //       label: "Delivered",
        //       icon: "check_circle",
        //       showPercentage: true
        //     },
        //     {
        //       key: "opened",
        //       label: "Open",
        //       icon: "open_in_new",
        //       showPercentage: true
        //     },
        //     {
        //       key: "clicked",
        //       label: "Click",
        //       icon: "mouse",
        //       showPercentage: true
        //     }
        //   ]
        // },

        // WhatsApp Campaign Delivery
        // {
        //   type: "list",
        //   title: "WhatsApp Campaign By Delivery",
        //   dataKey: "whatsappDelivery",
        //   size: "medium",
        //   items: [
        //     {
        //       key: "sent",
        //       label: "WhatsApp Campaign Sent",
        //       icon: "send",
        //       showPercentage: false
        //     },
        //     {
        //       key: "delivered",
        //       label: "Delivered",
        //       icon: "check_circle",
        //       showPercentage: true
        //     },
        //     {
        //       key: "read",
        //       label: "Read",
        //       icon: "visibility",
        //       showPercentage: true
        //     }
        //   ]
        // },

        // Client Portal & Company Email
        // {
        //   type: "simple",
        //   title: "Activations",
        //   dataKey: "activations",
        //   size: "medium",
        //   layout: "horizontal",
        //   metrics: [
        //     {
        //       key: "clientPortal",
        //       label: "Client Portal Activated",
        //       icon: "group",
        //       color: "red"
        //     },
        //     {
        //       key: "companyEmail",
        //       label: "Company Email Added",
        //       icon: "email",
        //       color: "blue"
        //     }
        //   ]
        // },

        // Data Import Table
        {
          type: "table",
          title: "Data Import",
          dataKey: "dataImport",
          size: "large",
          columns: [
            { key: "category", label: "Category" },
            { key: "total", label: "Total Imports", align: "center" },
            { key: "queued", label: "Queued", align: "center" },
            { key: "processing", label: "Processing", align: "center" },
            { key: "completed", label: "Completed", align: "center" },
            { key: "inProgress", label: "In-Progress", align: "center" },
            { key: "failed", label: "Failed", align: "center" }
          ]
        },

        // Reports Table
        // {
        //   type: "table",
        //   title: "Reports",
        //   dataKey: "reports",
        //   size: "large",
        //   columns: [
        //     { key: "type", label: "Report Type" },
        //     { key: "success", label: "Success Export", align: "center" },
        //     { key: "failed", label: "Failed Export", align: "center" }
        //   ]
        // },

        // Classroom - Graphical Dashboard
        {
          type: "simple",
          title: "Classroom",
          dataKey: "classroom",
          size: "large",
          layout: "graphical-dashboard",
          primaryMetrics: [
            {
              key: "totalSessions",
              label: "Total Sessions",
              icon: "school",
              color: "blue"
            },
            {
              key: "totalStudents",
              label: "Enrolled Students",
              icon: "group",
              color: "green"
            }
          ],
          progressItems: [
            {
              key: "notStarted",
              label: "Not Started",
              color: "gray",
              icon: "schedule"
            },
            {
              key: "inProgress",
              label: "In Progress",
              color: "blue",
              icon: "play_circle"
            },
            {
              key: "completed",
              label: "Completed",
              color: "purple",
              icon: "check_circle"
            },
            {
              key: "archived",
              label: "Archived",
              color: "green",
              icon: "archive"
            }
          ],
          showProgressBars: true,
          showDonutChart: true,
          chartType: "status"
        },

        // Automation - Graphical Dashboard
        {
          type: "simple",
          title: "Automation",
          dataKey: "automation",
          size: "medium",
          layout: "graphical-dashboard",
          primaryMetrics: [
            {
              key: "totalSetup",
              label: "Total Setup",
              icon: "flash_on",
              color: "yellow"
            },
            {
              key: "totalRuns",
              label: "Total Runs",
              icon: "settings",
              color: "purple"
            }
          ],
          statusItems: [
            {
              key: "active",
              label: "Active",
              color: "green",
              icon: "check_circle"
            },
            { key: "inactive", label: "Inactive", color: "red", icon: "cancel" }
          ],
          showStatusCards: true,
          showBarChart: true,
          chartType: "automation"
        },

        // // Events - Graphical Dashboard
        // {
        //   type: "simple",
        //   title: "Events",
        //   dataKey: "events",
        //   size: "medium",
        //   layout: "graphical-dashboard",
        //   primaryMetrics: [
        //     { key: "total", label: "Total Events", icon: "event", color: "blue" },
        //     { key: "contactsAdded", label: "Contacts Added", icon: "person_add", color: "green" }
        //   ],
        //   eventStatusItems: [
        //     { key: "published", label: "Published", color: "green", icon: "publish" },
        //     { key: "draft", label: "Draft", color: "yellow", icon: "edit" },
        //     { key: "archived", label: "Archived", color: "gray", icon: "archive" }
        //   ],
        //   showEventStatus: true,
        //   showStackedChart: true,
        //   chartType: "events"
        // },

        // Workflow Stats
        {
          type: "donut",
          title: "Workflow",
          dataKey: "workflow",
          size: "medium",
          centerValue: "total",
          centerLabel: "Workflow",
          segments: [
            { key: "active", label: "Active Workflow", color: "#10B981" },
            { key: "inactive", label: "Inactive Workflow", color: "#3B82F6" }
          ],
          showLegend: true
        },

        // // Invoice Table
        // {
        //   type: "table",
        //   title: "Invoice",
        //   dataKey: "invoice",
        //   size: "large",
        //   badge: "300 Invoices",
        //   badgeColor: "blue",
        //   columns: [
        //     { key: "type", label: "Invoice Type", align: "left" },
        //     { key: "paid", label: "Paid", align: "right" },
        //     { key: "unpaid", label: "Unpaid", align: "right" }
        //   ]
        // },

        // // Document Checklist
        // {
        //   type: "simple",
        //   title: "Document Checklist",
        //   dataKey: "documentChecklist",
        //   size: "medium",
        //   layout: "vertical",
        //   showProgressBar: true,
        //   metrics: [
        //     {
        //       key: "total",
        //       label: "Total Document Checklist",
        //       icon: "checklist",
        //       color: "blue"
        //     },
        //     {
        //       key: "active",
        //       label: "Active",
        //       icon: "check_circle",
        //       color: "green"
        //     },
        //     {
        //       key: "inactive",
        //       label: "Inactive",
        //       icon: "radio_button_unchecked",
        //       color: "gray"
        //     },
        //     {
        //       key: "activeTypes",
        //       label: "Active Document Type",
        //       icon: "folder",
        //       color: "purple"
        //     }
        //   ]
        // },

        // // Payment Schedule
        // {
        //   type: "simple",
        //   title: "Payment Schedule",
        //   dataKey: "paymentSchedule",
        //   size: "small",
        //   layout: "vertical",
        //   metrics: [
        //     {
        //       key: "created",
        //       label: "Total Payment Schedule Created",
        //       icon: "payment",
        //       color: "blue"
        //     }
        //   ]
        // },

        // Partners
        {
          type: "metric-grid",
          title: "Partners",
          dataKey: "partners",
          size: "medium",
          layout: "grid-1x4",
          metrics: [
            {
              key: "total",
              label: "Total Partner",
              icon: "business",
              color: "purple"
            },
            {
              key: "imported",
              label: "Imported from Database",
              icon: "cloud_download",
              color: "blue"
            },
            {
              key: "manual",
              label: "Added Manually",
              icon: "person_add",
              color: "green"
            }
          ]
        },
        // Products
        {
          type: "metric-grid",
          title: "Products",
          dataKey: "products",
          size: "medium",
          layout: "grid-1x4",
          metrics: [
            {
              key: "total",
              label: "Total Product",
              icon: "inventory",
              color: "purple"
            },
            {
              key: "imported",
              label: "Imported from Database",
              icon: "cloud_download",
              color: "blue"
            },
            {
              key: "manual",
              label: "Added Manually",
              icon: "add_box",
              color: "green"
            }
          ]
        },
        // Application with Complex Progress
        {
          type: "simple",
          title: "Application",
          dataKey: "applications",
          size: "medium",
          layout: "graphical-dashboard",
          showComplexProgress: true,
          metrics: [
            {
              key: "total",
              label: "Total Application",
              icon: "description",
              color: "blue"
            }
          ],
          progressItems: [
            { key: "discontinued", label: "Discontinued", color: "red" },
            { key: "completed", label: "Completed", color: "green" },
            { key: "inProgress", label: "In Progress", color: "purple" }
          ]
        },

        // Task with Module Progress
        {
          type: "simple",
          title: "Task",
          dataKey: "tasks",
          size: "medium",
          layout: "vertical",
          showModuleProgress: true,
          metrics: [
            {
              key: "total",
              label: "Total Task",
              icon: "task",
              color: "blue"
            }
          ],
          moduleItems: [
            { key: "inContacts", label: "Tasks in Contacts", color: "green" },
            {
              key: "inApplications",
              label: "Tasks in Applications",
              color: "yellow"
            }
          ]
        },

        // Document with Module Progress
        // {
        //   type: "simple",
        //   title: "Document",
        //   dataKey: "documents",
        //   size: "medium",
        //   layout: "vertical",
        //   showModuleProgress: true,
        //   metrics: [
        //     {
        //       key: "total",
        //       label: "Total Document",
        //       icon: "folder",
        //       color: "blue"
        //     }
        //   ],
        //   moduleItems: [
        //     {
        //       key: "inContacts",
        //       label: "Document in Contacts",
        //       color: "green"
        //     },
        //     {
        //       key: "inApplications",
        //       label: "Document in Applications",
        //       color: "yellow"
        //     },
        //     {
        //       key: "inClientPortal",
        //       label: "Document in Client Portal",
        //       color: "blue"
        //     },
        //     { key: "fromTask", label: "Document from Task", color: "purple" }
        //   ]
        // },

        // Notes & Terms
        {
          type: "simple",
          title: "Notes & Terms",
          dataKey: "notes",
          size: "medium",
          layout: "vertical",
          showModuleProgress: true,
          metrics: [
            {
              key: "total",
              label: "Total Notes",
              icon: "note",
              color: "blue"
            }
          ],
          moduleItems: [
            { key: "inContacts", label: "Notes in Contacts", color: "blue" },
            {
              key: "inApplications",
              label: "Notes in Applications",
              color: "blue"
            },
            {
              key: "inOfficeCheckin",
              label: "Notes in Office Check-in",
              color: "blue",
              isSmall: true
            },
            { key: "inAgents", label: "Notes in Agents", color: "green" },
            { key: "inPartners", label: "Notes in Partners", color: "yellow" }
          ]
        },
        // Agent Connect Grid
        {
          type: "metric-grid",
          title: "Agent Connect",
          dataKey: "agentConnect",
          size: "large",
          layout: "grid-3x3",
          metrics: [
            {
              key: "connected",
              label: "Connected Agent",
              icon: "people",
              color: "green"
            },
            {
              key: "applicationDisconnected",
              label: "Application Disconnected",
              icon: "assignment_turned_in",
              color: "red"
            },
            {
              key: "applicationsSent",
              label: "Applications Sent",
              icon: "send",
              color: "blue"
            },
            {
              key: "requestSent",
              label: "Request Sent",
              icon: "assignment",
              color: "amber"
            },
            {
              key: "documentsExchanged",
              label: "Documents Exchanged",
              icon: "folder_shared",
              color: "emerald"
            },
            {
              key: "agentDisconnected",
              label: "Agent Disconnected",
              icon: "person_off",
              color: "red"
            }
          ]
        }
      ];
    }
  },
  methods: {
    getCardComponent(type) {
      // Performance: Cache component lookups
      if (this.componentCache.has(type)) {
        return this.componentCache.get(type);
      }

      const componentMap = {
        simple: "SimpleStatCard",
        donut: "DonutStatCard",
        progress: "ProgressStatCard",
        list: "ListStatCard",
        table: "TableStatCard",
        chart: "ChartStatCard",
        "metric-grid": "MetricGridCard"
      };

      const component = componentMap[type] || "SimpleStatCard";
      this.componentCache.set(type, component);
      return component;
    },

    getCardData(dataKey) {
      // Performance: Cache data lookups
      if (this.dataCache.has(dataKey)) {
        return this.dataCache.get(dataKey);
      }

      const data = this.analytics[dataKey] || {};
      this.dataCache.set(dataKey, data);
      return data;
    },

    getCardClasses(card) {
      const sizeClasses = {
        small: "col-span-1",
        medium: "col-span-1 md:col-span-1",
        large: "col-span-1 md:col-span-2",
        xl: "col-span-1 md:col-span-3"
      };

      return [
        "stat-card-wrapper",
        sizeClasses[card.size] || sizeClasses.medium,
        card.className || ""
      ];
    },

    handleCardClick(cardData) {
      this.$emit("card-click", cardData);
    },

    // Method to add new stat card dynamically
    addStatCard(config) {
      this.customConfig.push(config);
    },

    // Method to remove stat card
    removeStatCard(index) {
      this.customConfig.splice(index, 1);
    },

    // Method to update card configuration
    updateCardConfig(index, newConfig) {
      this.$set(this.customConfig, index, {
        ...this.customConfig[index],
        ...newConfig
      });
    }
  },

  watch: {
    analytics: {
      handler() {
        // Clear data cache when analytics change
        this.dataCache.clear();
      },
      deep: true
    }
  }
};
</script>

<style scoped>
.stats-grid {
  width: 100%;
  max-width: 100%;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  width: 100%;
}

.stat-card-wrapper {
  transition: all 0.3s ease;
  animation: fadeInUp 0.6s ease-out;
}

.stat-card-wrapper:hover {
  transform: translateY(-2px);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive grid adjustments */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

@media (min-width: 1200px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  }
}

@media (min-width: 1600px) {
  .grid {
    grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  }
}
</style>
